import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Code,
  Group,
  Progress,
  Stack,
  Text,
} from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconCopy } from '@tabler/icons-react'

const TOTAL_SECONDS = 30

interface KeyRevealProps {
  keyValue: string
  onClose: () => void
}

export function KeyReveal({ keyValue, onClose }: KeyRevealProps) {
  const [remaining, setRemaining] = useState(TOTAL_SECONDS)
  const [copied, setCopied] = useState(false)

  const interval = useInterval(() => {
    setRemaining((prev) => Math.max(0, prev - 1))
  }, 1000)

  useEffect(() => {
    interval.start()
    return interval.stop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (remaining <= 0) {
      interval.stop()
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining])

  async function handleCopy() {
    await navigator.clipboard.writeText(keyValue)
    setCopied(true)
    notifications.show({ color: 'teal', message: 'Key copiada!' })
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Alert color="yellow" title="Nova API Key">
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Esta key sera exibida apenas uma vez. Copie e guarde em local seguro.
        </Text>

        <Group gap="xs" wrap="nowrap">
          <Code style={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {keyValue}
          </Code>
          <ActionIcon
            aria-label="Copiar key"
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            onClick={handleCopy}
          >
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </ActionIcon>
        </Group>

        <Progress
          value={(remaining / TOTAL_SECONDS) * 100}
          color="yellow"
          size="sm"
          aria-label="Tempo restante"
        />

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Ocultando em {remaining}s
          </Text>
          <Button variant="subtle" size="xs" onClick={onClose}>
            Fechar
          </Button>
        </Group>
      </Stack>
    </Alert>
  )
}
